import { Injectable } from '@angular/core';
import { CanActivate, UrlTree } from '@angular/router';
import { Observable, from, filter, firstValueFrom } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { selectAuthState } from 'pl-core-services';
import { take } from 'rxjs/operators';
import { AuthSyncService } from './auth-sync.service';

@Injectable({ providedIn: 'root' })
export class AuthSyncGuard implements CanActivate {
  private readonly DEBUG = environment.debug;

  private readonly SYNC_TIMEOUT = 500;

  private readonly STATE_SETTLE_DELAY = 100;

  private log(message: string, data?: any) {
    if (this.DEBUG) {
      // eslint-disable-next-line no-console
      console.log(`[AuthSyncGuard] ${message}`, data || '');
    }
  }

  constructor(
    private store: Store,
    private oidcSecurityService: OidcSecurityService,
    private authSyncService: AuthSyncService
  ) {}

  canActivate(): Observable<boolean | UrlTree> {
    return from(this.handleAuthSync());
  }

  private async handleAuthSync(): Promise<boolean> {
    this.log('Starting auth sync process');

    // Fastest path: check if we already have a valid token
    const initialToken = await firstValueFrom(
      this.oidcSecurityService.getAccessToken().pipe(take(1))
    );
    if (initialToken) {
      this.log('Found existing valid token');
      return true;
    }

    try {
      // Second priority: Check store state (still fast)
      const authState = await firstValueFrom(
        this.store.select(selectAuthState).pipe(
          filter((state) => state !== 'pending'),
          take(1)
        )
      );

      if (authState === 'success') {
        this.log('Auth state already success');
        return true;
      }
    } catch (error) {
      this.log('Error fetching auth state', error);
    }

    this.log('No existing token or auth state, attempting sync');

    // Try to sync auth state from other tabs
    try {
      const synced = await this.authSyncService.waitForAuthState(
        this.SYNC_TIMEOUT
      );

      if (synced) {
        this.log('Auth sync successful, waiting for state to settle');

        // Add a small delay to ensure OIDC internal state is updated
        await new Promise<void>((resolve) => {
          setTimeout(resolve, this.STATE_SETTLE_DELAY);
        });

        const syncedToken = await firstValueFrom(
          this.oidcSecurityService.getAccessToken().pipe(take(1))
        );
        if (syncedToken) {
          this.log('Token obtained after sync');

          // Double check auth state after sync
          const authState = await firstValueFrom(
            this.store.select(selectAuthState).pipe(
              filter((state) => state !== 'pending'),
              take(1)
            )
          );

          if (authState === 'success') {
            this.log('Auth state verified after sync');
            // Force a check but don't wait for it
            this.oidcSecurityService.checkAuth().subscribe();
            return true;
          }
        }
      }

      // If we get here, sync failed or wasn't successful
      this.log('Auth sync unsuccessful, allowing OIDC flow to proceed');
      this.oidcSecurityService.authorize();
      return false;
    } catch (error) {
      this.log('Error during auth sync process', error);
      this.oidcSecurityService.authorize();
      return false;
    }
  }
}
